import React, { useMemo, useCallback, useRef, useState } from "react";
import Icon1 from "@assets/icons/Icon";
import { usePlanningStore, CATEGORIES } from "@store/planningStore";
import { Appointment, AppointmentCategory } from "../../types/garage/planning";
import { getMonday, toISO } from "@store/disponibiliteStore";
import { snapToGrid, formatTime } from "@utils/timeGrid";
import OrdreModal from "@components/modals/OrdreModal";
import { findOrdreForPlate, buildOrdreFromAppointment } from "@store/ordreStore";
import { ordresApi } from "@services/api/ordres";
import type { Ordre } from "types/ordre";

const HOURS_START = 8;
const HOURS_END = 20;
const HOUR_BLOCK_HEIGHT = 128;
const SNAP_PX = 16;
const SNAP_HOUR = 0.125;

const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven"];

function getCategoryConfig(id: AppointmentCategory) {
  return CATEGORIES.find((c) => c.id === id)!;
}

interface DragState {
  apptId: string;
  mode: "move" | "resize";
  startY: number;
  startX: number;
  origStartHour: number;
  origDuration: number;
  origDate: string;
}

interface DragPreview {
  apptId: string;
  startHour: number;
  durationHours: number;
  date: string;
}

const CLICK_THRESHOLD = 5;

export function WeekTimeGridReception() {
  const selectedDate = usePlanningStore((s) => s.selectedDate);
  const activeCategories = usePlanningStore((s) => s.activeCategories);
  const appointments = usePlanningStore((s) => s.appointments);
  const searchQuery = usePlanningStore((s) => s.searchQuery);
  const updateAppointment = usePlanningStore((s) => s.updateAppointment);

  const [selectedOrdre, setSelectedOrdre] = useState<Ordre | null>(null);
  const [hoveredApptId, setHoveredApptId] = useState<string | null>(null);

  const monday = useMemo(() => getMonday(selectedDate), [selectedDate]);
  const weekDates = useMemo(
    () =>
      Array.from({ length: 5 }, (_, i) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        return toISO(d);
      }),
    [monday],
  );

  const hours = Array.from(
    { length: HOURS_END - HOURS_START },
    (_, i) => HOURS_START + i,
  );

  // Filter appointments for this week
  const weekAppointments = useMemo(() => {
    return appointments.filter(
      (a) =>
        weekDates.includes(a.date) &&
        activeCategories.includes(a.category) &&
        (searchQuery === "" ||
          a.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          a.plate.toLowerCase().includes(searchQuery.toLowerCase())),
    );
  }, [appointments, weekDates, activeCategories, searchQuery]);

  // Group by date
  const byDate = useMemo(() => {
    const map: Record<string, Appointment[]> = {};
    for (const date of weekDates) map[date] = [];
    for (const a of weekAppointments) {
      if (map[a.date]) map[a.date].push(a);
    }
    return map;
  }, [weekAppointments, weekDates]);

  const dragRef = useRef<DragState | null>(null);
  const [preview, setPreview] = useState<DragPreview | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const clickStartRef = useRef<{ x: number; y: number } | null>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, appt: Appointment, mode: "move" | "resize") => {
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      clickStartRef.current = { x: e.clientX, y: e.clientY };
      dragRef.current = {
        apptId: appt.id,
        mode,
        startY: e.clientY,
        startX: e.clientX,
        origStartHour: appt.startHour,
        origDuration: appt.durationHours,
        origDate: appt.date,
      };
      setPreview({
        apptId: appt.id,
        startHour: appt.startHour,
        durationHours: appt.durationHours,
        date: appt.date,
      });
    },
    [],
  );

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;

    const deltaY = e.clientY - d.startY;
    const snapDelta = Math.round(deltaY / SNAP_PX);

    if (d.mode === "move") {
      const newStart = snapToGrid(
        d.origStartHour + snapDelta * SNAP_HOUR,
        SNAP_HOUR,
      );
      const clamped = Math.max(
        HOURS_START,
        Math.min(newStart, HOURS_END - d.origDuration),
      );

      // Detect which day column the pointer is over
      let newDate = d.origDate;
      if (scrollRef.current) {
        const cols = scrollRef.current.querySelectorAll<HTMLElement>("[data-day-col]");
        cols.forEach((el) => {
          const rect = el.getBoundingClientRect();
          if (e.clientX >= rect.left && e.clientX < rect.right) {
            newDate = el.getAttribute("data-day-col") || d.origDate;
          }
        });
      }

      setPreview({
        apptId: d.apptId,
        startHour: clamped,
        durationHours: d.origDuration,
        date: newDate,
      });
    } else {
      const newDuration = snapToGrid(
        d.origDuration + snapDelta * SNAP_HOUR,
        SNAP_HOUR,
      );
      const clamped = Math.max(
        SNAP_HOUR,
        Math.min(newDuration, HOURS_END - d.origStartHour),
      );
      setPreview({
        apptId: d.apptId,
        startHour: d.origStartHour,
        durationHours: clamped,
        date: d.origDate,
      });
    }
  }, []);

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      const d = dragRef.current;
      const p = preview;
      const start = clickStartRef.current;

      const isClick =
        start &&
        Math.abs(e.clientX - start.x) < CLICK_THRESHOLD &&
        Math.abs(e.clientY - start.y) < CLICK_THRESHOLD &&
        d?.mode === "move";

      if (isClick && d) {
        const appt = appointments.find((a) => a.id === d.apptId);
        if (appt) {
          if (appt.ordre_id) {
            ordresApi
              .get(appt.ordre_id)
              .then((ordre) => setSelectedOrdre(ordre))
              .catch(() => {
                setSelectedOrdre(
                  findOrdreForPlate(appt.plate) ||
                    buildOrdreFromAppointment(appt),
                );
              });
          } else {
            setSelectedOrdre(
              findOrdreForPlate(appt.plate) ||
                buildOrdreFromAppointment(appt),
            );
          }
        }
      } else if (d && p) {
        updateAppointment(d.apptId, {
          startHour: p.startHour,
          durationHours: p.durationHours,
          date: p.date,
        });
      }
      dragRef.current = null;
      clickStartRef.current = null;
      setPreview(null);
    },
    [preview, updateAppointment, appointments],
  );

  const headerHeight = 28;
  const hourLabelWidth = 32;
  const minuteColWidth = 24;

  return (
    <div
      className="relative"
      style={{
        border: "1px solid #D9D9D9",
        borderRadius: 10,
        overflow: "hidden",
      }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      {/* Column headers — days */}
      <div className="flex flex-row" style={{ height: headerHeight }}>
        <div
          style={{
            width: hourLabelWidth + minuteColWidth + 1,
            borderRight: "1px solid #D9D9D9",
            borderBottom: "1px solid #D9D9D9",
          }}
        />
        {weekDates.map((date, i) => {
          const day = new Date(date + "T00:00:00").getDate();
          const isToday = date === toISO(new Date());
          return (
            <div
              key={date}
              className="flex-1 flex items-center justify-center text-sm font-medium whitespace-nowrap"
              style={{
                borderRight: "1px solid #D9D9D9",
                borderBottom: "1px solid #D9D9D9",
                background: isToday ? "#EEF2FF" : "#FFF",
                color: isToday ? "#6366F1" : "#525252",
                fontFamily: "Poppins",
              }}
            >
              {DAY_LABELS[i]} {day}
            </div>
          );
        })}
      </div>

      {/* Scrollable body */}
      <div ref={scrollRef} className="relative">
        <div className="flex flex-col">
          {hours.map((h) => (
            <div
              key={h}
              className="flex flex-row"
              style={{
                height: HOUR_BLOCK_HEIGHT,
                borderBottom: "1px solid #D9D9D9",
              }}
            >
              {/* Hour label */}
              <div
                className="flex items-start"
                style={{
                  width: hourLabelWidth,
                  paddingLeft: 7,
                  paddingTop: 2,
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: 12,
                  color: "#A1A1A1",
                  flexShrink: 0,
                }}
              >
                {h}h
              </div>

              {/* Minute column */}
              <div
                className="flex flex-col"
                style={{
                  width: minuteColWidth,
                  borderLeft: "1px solid #D9D9D9",
                  borderRight: "1px solid #D9D9D9",
                  flexShrink: 0,
                }}
              >
                {["00", "15", "30", "45"].map((m) => (
                  <div
                    key={m}
                    className="flex items-center justify-center"
                    style={{
                      height: 32,
                      borderBottom: "1px solid #D9D9D9",
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      fontSize: 12,
                      color: "#A1A1A1",
                    }}
                  >
                    {m}
                  </div>
                ))}
              </div>

              {/* Day columns */}
              {weekDates.map((date) => {
                const dayAppts = (byDate[date] || []).filter(
                  (a) => a.startHour >= h && a.startHour < h + 1,
                );

                const ghostInCell =
                  preview &&
                  preview.date === date &&
                  preview.startHour >= h &&
                  preview.startHour < h + 1
                    ? preview
                    : null;

                return (
                  <div
                    key={date}
                    data-day-col={date}
                    className="flex-1 relative"
                    style={{ borderRight: "1px solid #D9D9D9" }}
                  >
                    {/* Quarter-hour grid lines */}
                    {[0, 1, 2, 3].map((q) => (
                      <div
                        key={q}
                        style={{
                          height: 32,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div
                          style={{
                            height: 16,
                            borderBottom: "1px solid #D9D9D9",
                          }}
                        />
                        <div
                          style={{
                            height: 16,
                            background: "#F4F4F4",
                            borderBottom: "1px solid #D9D9D9",
                          }}
                        />
                      </div>
                    ))}

                    {/* Ghost preview */}
                    {ghostInCell &&
                      (() => {
                        const origAppt = appointments.find(
                          (a) => a.id === ghostInCell.apptId,
                        );
                        const config = origAppt
                          ? getCategoryConfig(origAppt.category)
                          : getCategoryConfig("rdv_divers");
                        const topPx =
                          ((ghostInCell.startHour - h) / SNAP_HOUR) * SNAP_PX;
                        const heightPx =
                          (ghostInCell.durationHours / SNAP_HOUR) * SNAP_PX;
                        return (
                          <div
                            className="absolute left-0 right-0 flex flex-col items-center justify-center pointer-events-none"
                            style={{
                              top: topPx,
                              height: Math.max(heightPx, 20),
                              background: config.bgColor,
                              border: `2px dashed ${config.color}`,
                              borderRadius: 5,
                              margin: "0 2px",
                              zIndex: 40,
                              opacity: 0.7,
                            }}
                          >
                            <span
                              className="text-h4 font-medium"
                              style={{
                                color: config.color,
                                lineHeight: "100%",
                              }}
                            >
                              {formatTime(ghostInCell.startHour)} –{" "}
                              {formatTime(
                                ghostInCell.startHour +
                                  ghostInCell.durationHours,
                              )}
                            </span>
                          </div>
                        );
                      })()}

                    {/* Appointments */}
                    {dayAppts.map((appt) => {
                      const config = getCategoryConfig(appt.category);
                      const topPx =
                        ((appt.startHour - h) / SNAP_HOUR) * SNAP_PX;
                      const heightPx =
                        (appt.durationHours / SNAP_HOUR) * SNAP_PX;
                      const isDragSource = preview?.apptId === appt.id;
                      const isHovered = hoveredApptId === appt.id;
                      return (
                        <div
                          key={appt.id}
                          className="absolute left-0 right-0 flex flex-col"
                          style={{
                            top: topPx,
                            height: Math.max(heightPx, 20),
                            background: config.bgColor,
                            border: `1px solid ${config.borderColor}`,
                            boxShadow: isHovered
                              ? "0px 4px 12px rgba(0, 0, 0, 0.25)"
                              : "0px 2px 4px rgba(0, 0, 0, 0.15)",
                            borderRadius: 5,
                            padding: "3px 4px",
                            overflow: "hidden",
                            zIndex: isDragSource ? 5 : isHovered ? 15 : 10,
                            margin: "0 2px",
                            cursor: "grab",
                            userSelect: "none",
                            opacity: isDragSource ? 0.35 : 1,
                            transition: isDragSource
                              ? "opacity 0.15s"
                              : "box-shadow 0.15s",
                          }}
                          onPointerDown={(e) =>
                            handlePointerDown(e, appt, "move")
                          }
                          onMouseEnter={() => setHoveredApptId(appt.id)}
                          onMouseLeave={() => setHoveredApptId(null)}
                        >
                          {/* Hover tooltip */}
                          {isHovered && !preview && (
                            <div
                              className="fixed z-50 bg-neutral-800 text-white rounded-lg px-3 py-2 pointer-events-none"
                              style={{
                                whiteSpace: "nowrap",
                                minWidth: 140,
                                transform: "translate(8px, 0)",
                              }}
                              ref={(el) => {
                                if (!el) return;
                                const parent = el.parentElement;
                                if (!parent) return;
                                const rect = parent.getBoundingClientRect();
                                el.style.top = `${rect.top}px`;
                                el.style.left = `${rect.right}px`;
                              }}
                            >
                              <div className="text-h4 font-medium">
                                {appt.clientName}
                              </div>
                              {appt.plate && (
                                <div className="text-h5 text-neutral-300">
                                  {appt.plate}
                                </div>
                              )}
                              <div className="text-h5 text-neutral-300">
                                {formatTime(appt.startHour)} —{" "}
                                {formatTime(
                                  appt.startHour + appt.durationHours,
                                )}
                              </div>
                              <div className="text-h5 text-neutral-400 mt-1">
                                Cliquer pour modifier
                              </div>
                            </div>
                          )}
                          <div
                            className="flex flex-row flex-1"
                            style={{
                              gap: 5,
                              minWidth: 0,
                              overflow: "hidden",
                            }}
                          >
                            <span
                              className="text-h4 font-medium"
                              style={{
                                lineHeight: "140%",
                                color: config.color,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                              }}
                            >
                              {appt.clientName}
                              {appt.plate ? ` ${appt.plate}` : ""}
                            </span>
                            <div
                              className="flex flex-row items-start"
                              style={{ flexShrink: 0 }}
                            >
                              {appt.hasCarIcon ? (
                                <Icon1
                                  name="car-reception"
                                  size={14}
                                  color={config.color}
                                />
                              ) : appt.hasMailIcon ? (
                                <Icon1
                                  name="car-restitution"
                                  size={14}
                                  color={config.color}
                                />
                              ) : null}
                            </div>
                          </div>
                          {heightPx > 30 && (
                            <span
                              className="text-h5"
                              style={{ color: config.color, opacity: 0.7 }}
                            >
                              {formatTime(appt.startHour)} –{" "}
                              {formatTime(appt.startHour + appt.durationHours)}
                            </span>
                          )}
                          {/* Resize handle */}
                          <div
                            className="absolute bottom-0 left-0 right-0 flex justify-center items-center"
                            style={{ height: 8, cursor: "ns-resize" }}
                            onPointerDown={(e) => {
                              e.stopPropagation();
                              handlePointerDown(e, appt, "resize");
                            }}
                          >
                            <div
                              style={{
                                width: 20,
                                height: 2,
                                borderRadius: 1,
                                background: config.color,
                                opacity: 0.4,
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {selectedOrdre && (
        <OrdreModal
          ordre={selectedOrdre}
          onClose={() => setSelectedOrdre(null)}
          onSave={() => setSelectedOrdre(null)}
        />
      )}
    </div>
  );
}
